// Compute the next canonical tracking number for SOW or Variation given
// the existing items on the contract and the contract's chosen number
// format. Two formats are supported:
//
//   "nn"             — plain zero-padded sequentials (`01`, `02`, ...).
//                      Two digits, no prefix, no year. The default for
//                      contracts that have not chosen a format.
//   "sow-yyyy-nnn"   — year-prefixed (`SOW-2026-001`).
//   "var-yyyy-nnn"   — year-prefixed (`VAR-2026-001`).
//
// The counter is fresh per format: items in a different shape are
// ignored when scanning for max(seq). This means switching format
// mid-contract starts a new counter, which is the deliberate user
// intent — the backend mirrors this behaviour.
//
// Pre-populating the canonical form on Add forms gives users the actual
// allocated shape before they submit, so the field's preview pill shows
// what the backend will allocate. The backend remains the source of
// truth — when the user accepts the auto value, `numberOverrideMode`
// stays false and the create payload omits the number so the backend's
// TRACKING_NUMBER sentinel allocates atomically.

export interface NumberedItem {
  number?: string;
}

export type NumberFormat = "nn" | "sow-yyyy-nnn" | "var-yyyy-nnn";

const YEAR_PATTERN_BY_PREFIX: Record<"SOW" | "VAR", RegExp> = {
  SOW: /^SOW-(\d{4})-(\d{3})$/,
  VAR: /^VAR-(\d{4})-(\d{3})$/,
};

const PLAIN_PATTERN = /^(\d+)$/;

const YEAR_FORMAT_FOR_PREFIX: Record<"SOW" | "VAR", NumberFormat> = {
  SOW: "sow-yyyy-nnn",
  VAR: "var-yyyy-nnn",
};

export function nextTrackingNumber(
  prefix: "SOW" | "VAR",
  items: readonly NumberedItem[],
  now: Date = new Date(),
  format: NumberFormat = YEAR_FORMAT_FOR_PREFIX[prefix],
): string {
  if (format === "nn") {
    let max = 0;
    for (const item of items) {
      if (!item.number) continue;
      const match = PLAIN_PATTERN.exec(item.number);
      if (!match) continue;
      const seq = Number(match[1]);
      if (seq > max) max = seq;
    }
    return String(max + 1).padStart(2, "0");
  }

  const year = now.getFullYear();
  const pattern = YEAR_PATTERN_BY_PREFIX[prefix];
  let max = 0;
  for (const item of items) {
    if (!item.number) continue;
    const match = pattern.exec(item.number);
    if (!match) continue;
    const itemYear = Number(match[1]);
    if (itemYear !== year) continue;
    const seq = Number(match[2]);
    if (seq > max) max = seq;
  }
  const next = String(max + 1).padStart(3, "0");
  return `${prefix}-${year}-${next}`;
}
